/**
 * Auth utility functions extracted from AuthContext to reduce complexity.
 * Contains: timeout signal polyfill, circuit breaker, environment detection.
 */

/**
 * Safari-compatible timeout signal polyfill.
 * AbortSignal.timeout is not supported in Safari Desktop.
 */
export const createTimeoutSignal = (ms) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ms);
  const originalAbort = controller.abort.bind(controller);
  controller.abort = () => {
    clearTimeout(timeoutId);
    originalAbort();
  };
  controller.signal.addEventListener('abort', () => clearTimeout(timeoutId));
  return controller.signal;
};

/**
 * Auth Circuit Breaker — exponential backoff for auth failures.
 * Prevents flooding the server with failed refresh requests.
 */
export const authCircuitBreaker = {
  failures: 0,
  lastFailure: 0,
  backoffMs: 1000,
  maxBackoffMs: 60000,
  isOpen: false,

  recordFailure() {
    this.failures++;
    this.lastFailure = Date.now();
    this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
    if (this.failures >= 3) {
      this.isOpen = true;
    }
  },

  recordSuccess() {
    this.failures = 0;
    this.backoffMs = 1000;
    this.isOpen = false;
  },

  canAttempt() {
    if (!this.isOpen) return true;
    const timeSinceFailure = Date.now() - this.lastFailure;
    return timeSinceFailure >= this.backoffMs;
  }
};

/**
 * Preview environment detection.
 */
export const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('emergent.host') ||
    hostname.includes('preview.emergentagent.com') ||
    hostname.includes('localhost');
};

export const PREVIEW_INACTIVITY_TIMEOUT = 3 * 60 * 1000;
